import { useCallback, useEffect, useRef, useState } from 'react';

import { workoutService } from '@personal-trainer-app/shared/src/services';
import { dayjs } from '@personal-trainer-app/shared/src/utils/date';
import {
  START_WORKOUT_COPY,
  answerArrived,
  answerFailed,
  answerRequested,
  initialAnswerState,
  pendingAnswerShown,
  rememberStartOptions,
  startOptionsFailureMessage,
} from '@personal-trainer-app/shared/src/utils/workout/start-options';

import type { StartWorkoutOptions } from '@personal-trainer-app/shared/src/types/domain/start-options.types';
import type { StartOptionsAnswerState } from '@personal-trainer-app/shared/src/utils/workout/start-options';

export { clearStartWorkoutOptionsCache } from '@personal-trainer-app/shared/src/utils/workout/start-options';

export interface UseStartWorkoutOptionsOptions {
  /**
   * Held false until the bolt is tapped, so the shell's mount pays nothing.
   * Flip it true to prefetch.
   */
  enabled?: boolean;
}

export interface UseStartWorkoutOptionsReturn {
  /** The answer on screen, fresh or remembered; null before the first one. */
  options: StartWorkoutOptions | null;
  /** True only while there is nothing to show yet. */
  isLoading: boolean;
  /**
   * Set when the request failed AND nothing was remembered. With a remembered
   * answer the failure is reported through `isStale` instead, because the sheet
   * has something to offer and an error frame would hide it.
   */
  error: string | null;
  /** The request failed and `options` is the last answer the app had. */
  isStale: boolean;
  /** When the answer on screen arrived, for the staleness line. */
  fetchedAt: dayjs.Dayjs | null;
  /**
   * A newer answer that landed while an older one was already on screen, and
   * that says something different.
   *
   * Held rather than applied. On one bar of signal slow is normal, and a list
   * that re-sorts under a thumb already reaching for a workout is worse than a
   * list a minute old. The caller offers it; the user chooses when to look.
   * Never set for the first answer, and never for an identical one.
   */
  pending: StartWorkoutOptions | null;
  /** Swap the pending answer in. A no-op when nothing is waiting. */
  showPending: () => void;
  refetch: () => Promise<void>;
}

/**
 * Everything the start-workout sheet needs, in one request.
 *
 * Fetches `GET /api/workout/start-options` and folds each result through the
 * shared answer rules (`utils/workout/start-options/start-answer`): a failure
 * falls back to the last answer the app had rather than to an error, and a
 * late answer that differs is held as `pending`. The mobile hook folds through
 * the same rules, so both platforms degrade alike; this file is the lifecycle.
 */
export function useStartWorkoutOptions({
  enabled = true,
}: UseStartWorkoutOptionsOptions = {}): UseStartWorkoutOptionsReturn {
  const [state, setState] =
    useState<StartOptionsAnswerState>(initialAnswerState);
  const [isLoading, setIsLoading] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchOptions = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setState(answerRequested);

    try {
      const options = await workoutService.getStartOptions();
      const answer = rememberStartOptions(options, dayjs().valueOf());

      if (!mountedRef.current) return;
      setState((current) => answerArrived(current, answer));
    } catch (err) {
      if (!mountedRef.current) return;

      const message = startOptionsFailureMessage(
        err,
        START_WORKOUT_COPY.fetchFailed
      );
      setState((current) => answerFailed(current, message));
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void fetchOptions();
  }, [fetchOptions]);

  const showPending = useCallback(() => {
    setState(pendingAnswerShown);
  }, []);

  return {
    options: state.shown?.options ?? null,
    isLoading: isLoading && state.shown === null,
    error: state.error,
    isStale: state.isStale,
    fetchedAt: state.shown ? dayjs(state.shown.at) : null,
    pending: state.pending?.options ?? null,
    showPending,
    refetch: fetchOptions,
  };
}

export default useStartWorkoutOptions;
